import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DropdownIcon, IncremnetDecrementIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";

import { QuoteSelectOption } from "../../../types";
import { useStyles } from "../styles";

export const CHECKBOX_SIZE = 24;

type BoxChildren = { children: React.ReactNode };
type FieldProps = {
  labelKey: string;
  value: string;
  onChange: (value: string) => void;
};

const Labeled = ({
  labelKey,
  className,
  children,
}: BoxChildren & { labelKey: string; className: string }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={className}>
      <Typography className={styles.formLabel}>{t(labelKey)}</Typography>
      {children}
    </Box>
  );
};

export const NumberedSectionTitle = ({
  number,
  titleKey,
}: {
  number: number;
  titleKey: string;
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.numberedSectionTitle}>
      <Box className={styles.sectionNumber}>{number}</Box>
      <Typography className={styles.sectionNumberLabel}>{t(titleKey)}</Typography>
    </Box>
  );
};

export const NumberField = ({
  labelKey,
  value,
  onChange,
  compact,
  narrow,
  grow,
  heightStepper,
  quantityStepper,
  shortStepper,
}: FieldProps & {
  compact?: boolean;
  narrow?: boolean;
  grow?: boolean;
  heightStepper?: boolean;
  quantityStepper?: boolean;
  shortStepper?: boolean;
}) => {
  const styles = useStyles();
  const step = (delta: number) => {
    const next = (Number(value) || 0) + delta;
    onChange(String(next < 0 ? 0 : next));
  };
  const field = shortStepper
    ? styles.formFieldStepperShort
    : quantityStepper
      ? styles.formFieldStepperQuantity
      : heightStepper
        ? styles.formFieldStepperHeight
        : grow
          ? styles.formFieldGrow
          : narrow
            ? styles.formFieldStepperNarrow
            : compact
              ? styles.formFieldStepperCompact
              : styles.formField;
  const box = shortStepper
    ? styles.stepperBoxShort
    : quantityStepper
      ? styles.stepperBoxQuantity
      : heightStepper
        ? styles.stepperBoxHeight
        : narrow
          ? styles.stepperBoxNarrow
          : compact
            ? styles.stepperBoxCompact
            : styles.stepperBox;

  return (
    <Labeled labelKey={labelKey} className={field}>
      <Box className={box}>
        <Box className={styles.stepperArrows}>
          <IncremnetDecrementIcon className={styles.stepperIcon} />
          <Box className={styles.stepperHitUp} onClick={() => step(1)} />
          <Box className={styles.stepperHitDown} onClick={() => step(-1)} />
        </Box>
        <Typography className={styles.stepperValue}>{value}</Typography>
      </Box>
    </Labeled>
  );
};

export const SelectField = ({
  labelKey,
  value,
  options,
  onChange,
  compact,
  grow,
}: FieldProps & {
  options: QuoteSelectOption[];
  compact?: boolean;
  grow?: boolean;
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const selected = options.find((option) => option.value === value);
  const field = compact
    ? styles.formFieldCompact
    : grow
      ? styles.formFieldGrow
      : styles.formField;

  return (
    <Labeled labelKey={labelKey} className={field}>
      <FormControl
        fullWidth={!compact}
        className={
          compact ? styles.formSelectControlCompact : styles.formSelectControl
        }
      >
        <Select
          className={compact ? styles.formSelectCompact : styles.formSelect}
          variant="outlined"
          displayEmpty
          value={value}
          IconComponent={DropdownIcon}
          onChange={(event) => onChange(event.target.value as string)}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: { className: styles.selectMenuPaper },
            MenuListProps: { className: styles.selectMenuList },
          }}
          renderValue={() => (
            <span className={styles.selectValue}>
              {selected ? t(selected.labelKey) : value}
            </span>
          )}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Labeled>
  );
};

export const NoteField = ({
  labelKey,
  value,
  onChange,
  placeholderKey,
  grow,
}: FieldProps & { placeholderKey?: string; grow?: boolean }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Labeled
      labelKey={labelKey}
      className={grow ? styles.formFieldGrow : styles.formField}
    >
      <TextField
        className={styles.noteInput}
        fullWidth
        variant="outlined"
        placeholder={placeholderKey ? t(placeholderKey) : ""}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Labeled>
  );
};
